/*
  Wash Club points calculator — a custom web component for blog-article content.

  Usage in an article body (HTML view):
    <wash-club-calculator>
      <p><em>[ fallback text shown if JS is unavailable ]</em></p>
    </wash-club-calculator>

  sections/main-article.liquid loads this asset only when an article's content
  contains "wash-club-calculator". The component renders in Shadow DOM so theme
  CSS and the calculator's styles can't affect each other; the element's own
  light-DOM content acts as the no-JS fallback and is hidden once mounted.

  Editor-proofing: Shopify's visual editor is known to unwrap unknown custom
  elements on save (it keeps their inner content). If that ever happens, switch
  the article back to a plain <div class="wash-club-calculator"> anchor — this
  script mounts the same calculator into those too.
*/
(function () {
  'use strict';

  if (!('customElements' in window)) return; // no support: fallback text stays visible
  if (customElements.get('wash-club-calculator')) return; // already defined

  var ACTIONS = [
    { label: 'Create an account', points: 50 },
    { label: 'Follow on TikTok', points: 10 },
    { label: 'Follow on Instagram', points: 10 },
    { label: 'Watch a tutorial', points: 20 },
    { label: 'Sign up for SMS', points: 50 },
    { label: 'Sign up for email', points: 50 },
    { label: 'First subscription bonus', points: 100 },
    { label: 'Fifth subscription bonus', points: 1000 },
    { label: 'Write a review', points: 100 },
    { label: 'Take the hair quiz', points: 20 },
    { label: '3 orders in a year', points: 100 },
    { label: 'Birthday reward', points: 100 }
  ];

  var CSS = [
    ':host{display:block;font:inherit;color:#1a1a1a;margin:8px 0 24px}',
    '*{box-sizing:border-box}',
    'h3{font-size:15px;font-weight:600;margin:0 0 10px;color:#4a4a4a}',
    '.wc-actions-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:8px;margin-bottom:24px}',
    '.wc-action{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 14px;border:1px solid #e2e0d8;font-size:13px;cursor:pointer;background:#fff;transition:border-color .15s ease}',
    '.wc-action:hover{border-color:#b4b2a9}',
    '.wc-label{display:flex;align-items:center;gap:8px}',
    '.wc-pts{color:#6b6a64;font-weight:600;white-space:nowrap;font-variant-numeric:tabular-nums}',
    'input[type=checkbox]{width:16px;height:16px;accent-color:#1a1a1a;cursor:pointer}',
    '.wc-purchase-block{margin-bottom:20px;padding:16px 20px;background:#f7f5f0;}',
    '.wc-purchase-row{display:flex;align-items:center;gap:12px;margin-bottom:12px}',
    '.wc-purchase-row .wc-lab{font-size:14px;color:#6b6a64;flex:1}',
    '.wc-purchase-row .wc-purchase-out{font-size:20px;font-weight:600;min-width:60px;text-align:right;font-variant-numeric:tabular-nums}',
    'input[type=range]{width:100%;margin-bottom:14px;accent-color:#1a1a1a;height:4px}',
    '.wc-sub-toggle{display:flex;align-items:flex-start;gap:10px;font-size:14px;cursor:pointer;line-height:1.5}',
    '.wc-sub-toggle input{margin-top:2px;flex-shrink:0}',
    '.wc-stats-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:12px}',
    '.wc-stat{background:#f7f5f0;padding:14px}',
    '.wc-stat.wc-accent{background:#e6f1fb}',
    '.wc-stat-label{font-size:12px;color:#6b6a64;margin:0 0 4px}',
    '.wc-accent .wc-stat-label{color:#0c447c}',
    '.wc-stat-value{font-size:22px;font-weight:600;margin:0;font-variant-numeric:tabular-nums}',
    '.wc-accent .wc-stat-value{color:#0c447c}',
    '.wc-redeem{display:flex;align-items:baseline;justify-content:space-between;padding:16px 20px;background:#fff;border:1px solid #e2e0d8;}',
    '.wc-redeem-label{font-size:14px;color:#6b6a64}',
    '.wc-redeem-value{font-size:22px;font-weight:600;font-variant-numeric:tabular-nums}',
    '@media (max-width:480px){.wc-stats-grid{grid-template-columns:1fr 1fr}.wc-stats-grid .wc-stat:last-child{grid-column:span 2}}'
  ].join(' ');

  var HTML =
    "<h3>Ways to earn — tap what you've done</h3>" +
    "<div class='wc-actions-grid'></div>" +
    "<div class='wc-purchase-block'>" +
      "<div class='wc-purchase-row'><span class='wc-lab' id='wc-purchase-lab'>Purchase amount</span><span class='wc-purchase-out'>$40</span></div>" +
      "<input type='range' min='0' max='200' step='5' value='40' class='wc-purchase' aria-labelledby='wc-purchase-lab'>" +
      "<label class='wc-sub-toggle'><input type='checkbox' class='wc-subscriber'><span>I'm a subscriber (4 pts per $1, plus up to 30% off, free shipping, early access, surprise gifts)</span></label>" +
    '</div>' +
    "<div class='wc-stats-grid' aria-live='polite'>" +
      "<div class='wc-stat'><p class='wc-stat-label'>Points to redeem</p><p class='wc-stat-value wc-action-points'>0</p></div>" +
      "<div class='wc-stat'><p class='wc-stat-label'>Purchase points</p><p class='wc-stat-value wc-purchase-points'>120</p></div>" +
      "<div class='wc-stat wc-accent'><p class='wc-stat-label'>Total points</p><p class='wc-stat-value wc-total-points'>120</p></div>" +
    '</div>' +
    "<div class='wc-redeem'><span class='wc-redeem-label'>Redeemable value (100 pts = $1)</span><span class='wc-redeem-value'>$1.20</span></div>";

  /* Render the calculator UI into a (shadow) root and wire up its behavior. */
  function render(root) {
    root.innerHTML = '<style>' + CSS + '</style>' + HTML;

    var grid = root.querySelector('.wc-actions-grid');
    ACTIONS.forEach(function (a) {
      var row = document.createElement('label');
      row.className = 'wc-action';
      var lab = document.createElement('span');
      lab.className = 'wc-label';
      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.setAttribute('data-points', String(a.points));
      lab.appendChild(cb);
      lab.appendChild(document.createTextNode(a.label));
      var pts = document.createElement('span');
      pts.className = 'wc-pts';
      pts.textContent = '+' + a.points.toLocaleString();
      row.appendChild(lab);
      row.appendChild(pts);
      grid.appendChild(row);
    });

    var slider = root.querySelector('.wc-purchase');
    var out = root.querySelector('.wc-purchase-out');
    var sub = root.querySelector('.wc-subscriber');
    var actionEl = root.querySelector('.wc-action-points');
    var purchEl = root.querySelector('.wc-purchase-points');
    var totalEl = root.querySelector('.wc-total-points');
    var redeemEl = root.querySelector('.wc-redeem-value');

    function recalc() {
      var purchase = Number(slider.value) || 0;
      out.textContent = '$' + purchase;
      var actionPoints = 0;
      grid.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
        if (cb.checked) actionPoints += Number(cb.getAttribute('data-points')) || 0;
      });
      var rate = sub.checked ? 4 : 3;
      var purchasePoints = Math.round(purchase * rate);
      var total = actionPoints + purchasePoints;
      actionEl.textContent = actionPoints.toLocaleString();
      purchEl.textContent = purchasePoints.toLocaleString();
      totalEl.textContent = total.toLocaleString();
      redeemEl.textContent = '$' + (total / 100).toFixed(2);
    }

    grid.addEventListener('change', recalc);
    slider.addEventListener('input', recalc);
    sub.addEventListener('change', recalc);
    recalc();
  }

  /* The custom element. Fallback light-DOM content is hidden once the shadow
     root renders; the _wcMounted guard keeps re-connection (e.g. the element
     being moved in the DOM) from double-initializing. */
  class WashClubCalculator extends HTMLElement {
    connectedCallback() {
      if (this._wcMounted) return;
      this._wcMounted = true;
      render(this.attachShadow({ mode: 'open' }));
    }
  }
  customElements.define('wash-club-calculator', WashClubCalculator);

  /* Legacy / editor-proof path: also mount into plain div anchors. */
  function mountAnchors() {
    document.querySelectorAll('div.wash-club-calculator').forEach(function (el) {
      if (el.getAttribute('data-wc-mounted')) return;
      el.setAttribute('data-wc-mounted', '1');
      var root;
      try { root = el.attachShadow({ mode: 'open' }); }
      catch (e) { el.textContent = ''; root = el; }
      render(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAnchors);
  else mountAnchors();
})();
